import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@shared/components/ui/dialog';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Loader2, History, CheckCircle2, XCircle, Monitor, Smartphone, Laptop } from 'lucide-react';
import { supabase } from '@shared/supabaseClient';

// Helper to parse user agent for device info
const getDeviceInfo = (userAgent) => {
  if (!userAgent) return { device: 'Unknown', icon: Monitor };
  
  const ua = userAgent.toLowerCase();
  if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
    return { device: 'Mobile', icon: Smartphone };
  }
  if (ua.includes('tablet') || ua.includes('ipad')) {
    return { device: 'Tablet', icon: Laptop };
  }
  return { device: 'Desktop', icon: Monitor };
};

// Helper to get browser name from user agent
const getBrowser = (userAgent) => {
  if (!userAgent) return 'Unknown';
  
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Edg')) return 'Edge';
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Opera') || userAgent.includes('OPR')) return 'Opera';
  return 'Browser';
};

const LoginHistoryModal = ({ isOpen, onClose, user }) => {
  const [loginHistory, setLoginHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchLoginHistory = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('login_history')
        .select('*')
        .eq('user_id', user.dbId || user.id)
        .order('login_time', { ascending: false })
        .limit(5);

      if (error) throw error;
      setLoginHistory(data || []);
    } catch (err) {
      console.error('Failed to fetch login history:', err);
      setLoginHistory([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && user) {
      fetchLoginHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user]);

  const formatLoginMethod = (method) => {
    switch (method) {
      case 'password': return 'Password';
      case 'mfa_totp': return 'Authenticator App';
      case 'mfa_webauthn': return 'Passkey';
      default: return method || 'Unknown';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[calc(100%-1rem)] sm:w-full max-w-2xl max-h-[85vh] sm:max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-primary" />
            Login History
          </DialogTitle>
          <DialogDescription>
            Recent login activity for {user?.name || user?.email}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : loginHistory.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No login history found.</p>
              <p className="text-sm">Login events will appear here after the user logs in.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {loginHistory.map((entry) => {
                const { device, icon: DeviceIcon } = getDeviceInfo(entry.user_agent);
                const browser = getBrowser(entry.user_agent);
                const loginDate = new Date(entry.login_time);
                
                return (
                  <div
                    key={entry.id}
                    className="p-3 bg-muted/30 rounded-lg border border-border flex items-center gap-4"
                  >
                    {/* Success/Failure indicator */}
                    <div className="flex-shrink-0">
                      {entry.success ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                    </div>

                    {/* Device icon */}
                    <div className="flex-shrink-0">
                      <DeviceIcon className="w-5 h-5 text-muted-foreground" />
                    </div>

                    {/* Main info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">
                          {loginDate.toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </span>
                        <span className="text-muted-foreground text-sm">
                          {loginDate.toLocaleTimeString('en-GB', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {device} • {browser}
                      </div>
                    </div>

                    {/* Login method badge */}
                    <Badge variant="outline" className="flex-shrink-0 text-xs">
                      {formatLoginMethod(entry.login_method)}
                    </Badge>

                    {/* Failure reason if any */}
                    {!entry.success && entry.failure_reason && (
                      <span className="text-xs text-red-500 truncate max-w-[100px]" title={entry.failure_reason}>
                        {entry.failure_reason}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="pt-4 border-t border-border flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LoginHistoryModal;
